import { H4, Text } from '@payaca/ambrosia';
import useUpdateProject from '@/api/mutations/project/useUpdateProject';
import { graphql } from '@/gql';
import { usePermissions } from '@/hooks/usePermissions';
import AutoSaveForm from '@/ui/components/autoSaveForm/AutoSaveForm';
import InlineSwitchField from '@/ui/components/inlineSwitchField/InlineSwitchField';
import OptionsSectionSkeleton from '@/ui/components/optionsSectionSkeleton/OptionsSectionSkeleton';
import { ResultOf } from '@graphql-typed-document-node/core';
import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';

export const PriceDetailOptions_Project = graphql(`
  fragment PriceDetailOptions_Project on Project {
    id
    config {
      hideItemPrices
      hideTaxBreakdown
    }
  }
`);

export type TProps = {
  /** Public id of the Project whose price-detail options are being edited. */
  projectId: string;
  /** Called after each save, so the caller can reload its document preview. */
  onSaved: () => void;
  /** Supporting text shown under the heading. */
  description?: string;
  /** Project data supplying the current option values (from the page's root query). */
  project?: ResultOf<typeof PriceDetailOptions_Project>;
  className?: string;
};

type FormValues = {
  hideItemPrices: boolean;
  hideTaxBreakdown: boolean;
};

/**
 * Auto-saving form of the Project's price-detail toggles (hide item prices,
 * hide tax breakdown). Rendered only once its data has loaded, so the form
 * seeds its defaults from the persisted values.
 */
const PriceDetailOptionsForm: FC<
  Omit<TProps, 'project'> & {
    config: ResultOf<typeof PriceDetailOptions_Project>['config'];
  }
> = ({ projectId, onSaved, description, config, className }) => {
  const { mutate: updateProject } = useUpdateProject();

  const methods = useForm<FormValues>({
    defaultValues: {
      hideItemPrices: config.hideItemPrices,
      hideTaxBreakdown: config.hideTaxBreakdown,
    },
  });

  return (
    <AutoSaveForm
      methods={methods}
      className={clstx('flex flex-col gap-3', className)}
      // do not debounce submissions. due to only having checkboxes in the form
      debounceMs={0}
      onSubmit={(values) =>
        updateProject({ projectId, config: values }, { onSuccess: onSaved })
      }
    >
      <div>
        <H4>Price detail</H4>
        {description && <Text level="supporting">{description}</Text>}
      </div>

      <div className="flex flex-col gap-4">
        <Controller<FormValues, 'hideItemPrices'>
          name="hideItemPrices"
          render={({ field }) => (
            <InlineSwitchField
              name={field.name}
              label="Hide item prices"
              tooltip="Show only the totals, hiding line item and section prices"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />

        <Controller<FormValues, 'hideTaxBreakdown'>
          name="hideTaxBreakdown"
          render={({ field }) => (
            <InlineSwitchField
              name={field.name}
              label="Hide tax breakdown"
              tooltip="Hide the subtotal and tax, showing only the total including tax"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>
    </AutoSaveForm>
  );
};

/**
 * Lets the user control how much price detail the Project's documents show
 * the customer. Hidden entirely for users without permission to manage price
 * visibility.
 */
const PriceDetailOptions: FC<TProps> = (props) => {
  const { project, ...formProps } = props;

  const { userHasRequiredPermission } = usePermissions();
  const canManagePriceVisibility = userHasRequiredPermission([
    DealsPermissions.MANAGE_PRICE_VISIBILITY,
  ]);

  if (!canManagePriceVisibility) return null;

  // Show a skeleton until the data behind the rendered state has settled, so we
  // never flash default values (unchecked toggles).
  const config = project?.config;
  if (!config) {
    return (
      <OptionsSectionSkeleton
        title="Price detail"
        className={formProps.className}
      />
    );
  }

  return <PriceDetailOptionsForm {...formProps} config={config} />;
};

export default PriceDetailOptions;
